import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import SignaturePad from 'signature_pad';

import { AppDispatch, RootState } from '../../core/data/redux/store';
import { getAllTemplate } from '../../core/data/redux/action';
import AddressProofValidation from '../../core/data/modals/addressProofValidation';
import Loader from '../../core/data/img/car_loading.json';
import PreliminaryAgreement from '../../core/data/modals/preliminaryAgreement';
import Selfie from '../../core/data/modals/selfie';
import ImageSlider from '../../feature-module/common/uploadedImage';

import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import type { Slide } from 'yet-another-react-lightbox';

const BookingDetail = ({
  register,
  errors,
  watch,
  prevStep,
  isSubmitted,
  setValue,
}: any) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const signRef = useRef<HTMLCanvasElement | null>(null);
  const [licenseFiles, setLicenseFiles] = useState<Attachment[]>([]);
  const [selfieFiles, setSelfieFiles] = useState<Attachment[]>([]);
  const [addressProofFiles, setAddressProofFiles] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 767);
  const dispatch = useDispatch<AppDispatch>();

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxSlide, setLightboxSlide] = useState<Slide[]>([]);

  const {
    bookingsLoading,
    systemSettings,
    vehicleRecommendationData,
    user,
    bookingTotalAmount,
    carDetail,
    templateList,
  } = useSelector((state: RootState) => ({
    templateList: state.template.templateList,
    carDetail: state.vehicle.carDetail,
    bookingTotalAmount: state.booking.bookingTotalAmount,
    bookingsLoading: state.booking.bookingsLoading,
    user: state.auth.user,
    vehicleRecommendationData:
      state.vehicleRecommendation.vehicleRecommendationData,
    systemSettings: state.auth.systemSettings,
  }));

  useEffect(() => {
    dispatch(getAllTemplate());
    setValue('attachments', []);
    setValue('billing_terms_conditions', false);
  }, []);

  interface Attachment {
    file: File;
    name: string;
    label: string;
    type: 'selfie' | 'upload';
  }

  const attachments: Attachment[] = watch('attachments') || [];
  const [rotation, setRotation] = useState(0);

  const removeFile = (
    fileName: string,
    type: 'license' | 'selfie' | 'address_proof'
  ) => {
    const updatedLicenseFiles =
      type === 'license'
        ? licenseFiles.filter((file) => file.name !== fileName)
        : licenseFiles;

    const updatedSelfieFiles =
      type === 'selfie'
        ? selfieFiles.filter((file) => file.name !== fileName)
        : selfieFiles;

    const updatedAddresssProoFiles =
      type === 'address_proof'
        ? addressProofFiles.filter((file) => file.name !== fileName)
        : addressProofFiles;

    if (type === 'license') {
      setLicenseFiles(updatedLicenseFiles);
    } else if (type === 'address_proof') {
      setAddressProofFiles(updatedAddresssProoFiles);
    } else if (type === 'selfie') {
      setSelfieFiles(updatedSelfieFiles);
    }

    const updatedAttachments = [
      ...attachments.filter((att) => att.label === 'signature'),
      ...updatedAddresssProoFiles,
      ...updatedLicenseFiles,
      ...updatedSelfieFiles,
    ];

    setValue('attachments', updatedAttachments, { shouldValidate: true });
  };

  interface Attachment {
    file: File;
    name: string;
    label: string;
    type: 'selfie' | 'upload';
  }

  const onDropLicense = (acceptedFiles: File[]) => {
    const newAttachments: Attachment[] = acceptedFiles.map((file, index) => {
      const extension = file.name.split('.').pop();

      const fileName = `${user?.first_name}_license_${user?.id}_${vehicleRecommendationData?.bookingInfo?.id ?? carDetail?.vin}_${licenseFiles.length + index + 1}.${extension}`;

      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      return {
        file: renamedFile,
        name: renamedFile.name,
        label: 'driving_license',
        type: 'upload',
      };
    });

    setLicenseFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setValue('attachments', [...attachments, ...newAttachments], {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      const orientation = window.screen.orientation?.type || window.orientation;

      if (orientation === 'landscape-primary') {
        setRotation(0);
      } else if (orientation === 'landscape-secondary') {
        setRotation(180);
      } else if (orientation === 'portrait-secondary') {
        setRotation(180);
      } else {
        setRotation(0);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    handleOrientationChange();

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const onDropAddressProof = (acceptedFiles: File[]) => {
    const newAttachments: Attachment[] = acceptedFiles.map((file, index) => {
      const extension = file.name.split('.').pop();
      const fileName = `${user?.first_name}_address_proof_${user?.id}_${vehicleRecommendationData?.bookingInfo?.id ?? carDetail?.vin}_${licenseFiles.length + index + 1}.${extension}`;

      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      return {
        file: renamedFile,
        name: renamedFile.name,
        label: 'address_proof',
        type: 'upload',
      };
    });

    setAddressProofFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setValue('attachments', [...attachments, ...newAttachments], {
      shouldValidate: true,
    });
  };

  const {
    getRootProps: getLicenseRootProps,
    getInputProps: getLicenseInputProps,
  } = useDropzone({
    onDrop: onDropLicense,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    multiple: true,
  });

  const {
    getRootProps: getAddressProofRootProps,
    getInputProps: getAddressProofInputProps,
  } = useDropzone({
    onDrop: onDropAddressProof,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    multiple: true,
  });

  interface Attachment {
    file: File;
    name: string;
    label: string;
    type: 'selfie' | 'upload';
  }

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const selfieIndex = selfieFiles.length + 1;
            const fileName = `${user?.first_name}_selfie_${user?.id}_${vehicleRecommendationData?.bookingInfo?.id ?? carDetail?.vin}_${selfieIndex}.png`;

            const selfieFile = new File([blob], fileName, {
              type: 'image/png',
            });
            const newAttachment: Attachment = {
              file: selfieFile,
              name: selfieFile.name,
              label: 'driving_license',
              type: 'selfie',
            };

            setValue('attachments', [...attachments, newAttachment], {
              shouldValidate: true,
            });
            setSelfieFiles((prevFiles) => [...prevFiles, newAttachment]);
          }
        }, 'image/png');

        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream?.getTracks();
        tracks?.forEach((track) => track.stop());
        setIsCameraOpen(false);
      }
    }
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      const orientation = window.screen.orientation?.type || window.orientation;

      if (orientation === 'landscape-primary') {
        setRotation(0); // normal
      } else if (orientation === 'landscape-secondary') {
        setRotation(180); // upside down
      } else if (orientation === 'portrait-secondary') {
        setRotation(180); // upside down
      } else {
        // portrait-primary
        setRotation(0); // normal
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    handleOrientationChange(); // Initial run

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const handleSelfieCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const newAttachments: Attachment[] = Array.from(files).map((file) => ({
        file: file,
        name: file.name,
        label: 'driving_license',
        type: 'selfie',
      }));

      setValue('attachments', [...attachments, newAttachments], {
        shouldValidate: true,
      });

      setSelfieFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    }
  };

  // const [showTerms, setShowTerms] = useState<boolean>(false); scrolledToBottom,
  const [isAccepted, setIsAccepted] = useState(false);
  const termsRef = useRef<HTMLDivElement | null>(null);
  const [Error, setError] = useState('');
  // const getAddress = () => {
  //   dispatch(getAddressFromCoords());
  // }

  const openCamera = async () => {
    setIsCameraOpen(true);
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      setError(
        'Permission denied when accessing the camera. Please enable camera access in device settings.'
      );
      console.error('Error accessing camera:', error);
    }
  };

  const signaturePad = useRef<SignaturePad | null>(null);
  const savedSignature = useRef<any>(null);

  const clearSignature = () => {
    if (signaturePad.current) {
      signaturePad.current.clear();
      savedSignature.current = null;
    }

    setValue('billing_terms_conditions', false,  { shouldValidate: true });
  };

  useEffect(() => {
    if (isAccepted && signRef.current) {
      signaturePad.current = new SignaturePad(signRef.current);
    }

    if (isAccepted && savedSignature.current && signaturePad.current) {
      signaturePad.current.fromData(savedSignature.current);
    }
  }, [isAccepted]);

  useEffect(() => {
    return () => {
      if (signaturePad.current && !isAccepted) {
        savedSignature.current = signaturePad.current.toData();
      }
    };
  }, [isAccepted]);

  const handleAccept = () => {
    if (signaturePad.current?.isEmpty()) {
      setValue('billing_terms_conditions', false, { shouldValidate: true });
      return;
    }

    setValue('billing_terms_conditions', isAccepted, { shouldValidate: true });

    const dataURL = signaturePad.current?.toDataURL('image/png');
    const blob = dataURLToBlob(dataURL);

    if (blob) {
      const fileName = `${user?.first_name}_signature_${user?.id}_${vehicleRecommendationData?.bookingInfo?.id ?? carDetail?.vin}.png`;
      const signFile = new File([blob], fileName, { type: 'image/png' });

      const newAttachment: Attachment = {
        file: signFile,
        name: signFile.name,
        label: 'signature',
        type: 'upload',
      };

      const updatedAttachments = [
        ...attachments.filter((att) => att.label !== 'signature'),
        newAttachment,
      ];

      setValue('attachments', updatedAttachments, { shouldValidate: true });

      if (Object.entries(errors)?.length === 0) {
        document.getElementById('booking_form_submit')?.click();
      }
    } else {
      console.error('Failed to convert data URL to blob.');
    }
  };

  const dataURLToBlob = (dataURL: any) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleScroll = () => {
    const el = termsRef.current;
    if (!el) return;

    const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 300;
    setShowScrollTop(isBottom);
    setIsAccepted(isBottom);
  };

  const scrollToTop = () => {
    if (termsRef.current) {
      termsRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const scrollToBottom = () => {
    if (termsRef.current) {
      termsRef.current.scrollTo({
        top: termsRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const signatureDataURL = signaturePad.current?.toDataURL('image/png');

  const getAttachmentStatus = (captured_type: any, label: any): string => {
    const matchingAttachments =
      attachments?.filter(
        (att: any) =>
          att?.type === captured_type && att?.label === label && att?.file
      ) || [];

    if (matchingAttachments.length === 0) {
      return 'not-uploaded';
    }

    return matchingAttachments

      .map((att, index) => {
        const imgURL = URL.createObjectURL(att.file);
        return `<img key="${index}" src="${imgURL}" alt="${label} preview ${index + 1}" style="width: 300px;  object-fit: cover; margin: 10px;" />`;
      })

      .join('');
  };

  const bookingData = useMemo(
    () => ({
      logo: '<img class="img-fluid" src="/assets/img/logo/fairRentallogo-small.png" alt="Logo" style="width: 35%;">',
      startDate:
        dayjs(watch('booking_start_date')).format('MM-DD-YYYY') +
        ' ' +
        watch('booking_start_time'),
      endDate:
        dayjs(watch('booking_return_date')).format('MM-DD-YYYY') +
        ' ' +
        watch('booking_return_time'),
      bookingPrice: `${bookingTotalAmount?.initialRentalCost}${
        bookingTotalAmount?.paymentMethod?.name === 'Daily'
          ? '/Day'
          : bookingTotalAmount?.paymentMethod?.name === 'Weekly'
            ? '/Week'
            : '/Full'
      }`,
      rentalPlan: bookingTotalAmount?.paymentMethod?.name,
      securityDeposit: `${bookingTotalAmount?.feeTypes?.find((item: any) => item.fee_type === 'Security Deposit')?.calcAmount}
    ${
      bookingTotalAmount?.feeTypes?.find(
        (item: any) => item.fee_type === 'Security Deposit'
      )?.reason
        ? ' (' +
          bookingTotalAmount?.feeTypes?.find(
            (item: any) => item.fee_type === 'Security Deposit'
          )?.reason +
          ')'
        : ''
    }`,
      includedDistance: 'Unlimited miles',
      pickupLocation: systemSettings?.default_pickup_location,
      protectionPlan: 'Default',
      plateNumber: carDetail?.plate_number,
      make: carDetail?.make,
      model: carDetail?.model,
      vin: carDetail?.vin,
      year: carDetail?.year,
      color: carDetail?.color,
      mileage: carDetail?.mileage,
      fuelType: carDetail?.fuel_type,
      driverName: watch('billing_last_name')
        ? watch('billing_first_name') + ' ' + watch('billing_last_name')
        : watch('billing_first_name'),
      signature: signatureDataURL
        ? `<img src="${signatureDataURL}" style="width: 50px;" />`
        : ' ',

      selfie: getAttachmentStatus('selfie', 'driving_license'),
      license: getAttachmentStatus('upload', 'driving_license'),
      addressProof: getAttachmentStatus('upload', 'address_proof'),
      approveDate: dayjs().format('MM-DD-YYYY'),
      payment_model: vehicleRecommendationData?.payment_model,
      internalpictures: 'not-uploaded',
      externalpictures: 'not-uploaded',
      odometerpictures: 'not-uploaded',
      underhoodpictures: 'not-uploaded',
      oilchangestickerpictures: 'not-uploaded',
      registrationstickerpictures: 'not-uploaded',
      tollstickerpictures: 'not-uploaded',
      sparetyrepictures: 'not-uploaded',
      checkinCarImage: 'not-uploaded',

      selfieClass:
        getAttachmentStatus('selfie', 'driving_license') === 'not-uploaded'
          ? 'not-uploaded'
          : '',
      licenseClass:
        getAttachmentStatus('upload', 'driving_license') === 'not-uploaded'
          ? 'not-uploaded'
          : '',
      addressProofClass:
        getAttachmentStatus('upload', 'address_proof') === 'not-uploaded'
          ? 'not-uploaded'
          : '',
      internalpicturesClass: 'not-uploaded',
      externalpicturesClass: 'not-uploaded',
      odometerpicturesClass: 'not-uploaded',
      underhoodpicturesClass: 'not-uploaded',

      oilchangestickerpicturesClass: 'not-uploaded',
      registrationstickerpicturesClass: 'not-uploaded',
      tollstickerpicturesClass: 'not-uploaded',
      sparetyrepicturesClass: 'not-uploaded',

      checkinCarImageClass: 'not-uploaded',
    }),
    [
      signRef,
      watch('booking_start_date'),
      watch('booking_return_date'),
      watch('billing_last_name'),
      watch('billing_first_name'),
      watch('booking_start_time'),
      watch('booking_return_time'),
      bookingTotalAmount,
      signatureDataURL,
      attachments,
    ]
  );

  const BookingAgreement: React.FC = () => {
    const replacePlaceholders = (
      template: string,
      data: Record<string, any>
    ) => {
      return template?.replace(/\{(\w+)\}/g, (_, key) => {
        return data[key] || 'N/A';
      });
    };

    const filledContent = replacePlaceholders(
      templateList?.find((item: any) => item.slug === 'preliminary_contract')
        ?.content,
      bookingData
    );

    return <div dangerouslySetInnerHTML={{ __html: filledContent }} />;
  };

  return (
    <>
      <div className="col-12 px-2">
        <div
          {...getLicenseRootProps()}
          style={{
            border:
              errors.attachments && licenseFiles?.length === 0
                ? '2px dashed red'
                : licenseFiles?.length === 0
                  ? '2px dashed #737373'
                  : '2px dashed #ccc',
            boxShadow:
              licenseFiles?.length === 0
                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                : 'none',

            padding: '8px',
            cursor: 'pointer',
            marginBottom: '10px',
            textAlign: 'center',
          }}
        >
          <input {...getLicenseInputProps()} />

          <p className="py-1 mb-0">
            {isMobile
              ? 'Click to upload your License(s)'
              : 'Drag & drop your License(s) here, or Click to Select'}
          </p>
        </div>
        {/* <ul>
          {licenseFiles?.map((file) => (
            <li key={file.name} className="mb-1">
              <img
                src={URL.createObjectURL(file.file)}
                width={200}
                height={200}
              />{' '}
              <br />
              {file.name}{' '}
              <span
                style={{ color: 'red', cursor: 'pointer' }}
                onClick={() => removeFile(file.name, 'license')}
              >
                {' '}
                <i className="feather icon-x-circle " />
              </span>
            </li>
          ))}
        </ul> */}

        {licenseFiles?.length > 0 && (
          <ImageSlider
            selectedFiles={licenseFiles}
            name="license"
            removeFile={removeFile}
            setLightboxSlide={setLightboxSlide}
            setLightboxIndex={setLightboxIndex}
          />
        )}
      </div>

      <div className="col-12 mb-3 px-2">
        <div
          onClick={openCamera}
          style={{
            border:
              errors.attachments && selfieFiles?.length === 0
                ? '2px dashed red'
                : licenseFiles?.length > 0 && selfieFiles?.length === 0
                  ? '2px dashed #737373'
                  : '2px dashed #ccc',
            boxShadow:
              licenseFiles?.length > 0 && selfieFiles?.length === 0
                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                : 'none',
            padding: '8px',
            cursor: 'pointer',
            marginBottom: '10px',
            textAlign: 'center',
          }}
        >
          <p className="py-1 mb-0">
            {isMobile
              ? 'Click to take Selfie(s) with your License'
              : 'Drag & drop your Selfie(s) with your License, or Click to Select'}
          </p>
        </div>

        <div className="text-center mb-2">
          {isCameraOpen && (
            <div>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  marginTop: '10px',
                  transformOrigin: 'center center',
                  transform: `rotate(${rotation}deg) scaleX(-1)`,
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              <p className="text-danger">{Error}</p>
              <button
                type="button"
                className="mt-2 p-2 border-0"
                onClick={captureSelfie}
                style={{ cursor: 'pointer' }}
              >
                📸 Capture Selfie
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleSelfieCapture}
            style={{ display: 'none' }}
          />
        </div>

        {/* <ul> {selfieFiles?.map((file) => (
            <li key={file.name} className="mb-1">
              <img
                src={URL.createObjectURL(file?.file)}
                width={200}
                height={200}
              />{' '}
              <br />
              {file.name}{' '}
              <span
                style={{ color: 'red', cursor: 'pointer' }}
                onClick={() => removeFile(file.name, 'selfie')}
              >
                {' '}
                <i className="feather icon-x-circle " />
              </span>
            </li>
          ))}  </ul>*/}

        {selfieFiles?.length > 0 && (
          <ImageSlider
            selectedFiles={selfieFiles}
            name="selfie"
            removeFile={removeFile}
            setLightboxSlide={setLightboxSlide}
            setLightboxIndex={setLightboxIndex}
          />
        )}
      </div>

      <div className="col-12 px-2 pt-1 mb-0">
        {/* <h6 className="align-items-center mb-1">
          <span className="pe-1" style={{ color: "#111" }}  >License with Selfie  </span>

          <FaInfoCircle

            data-bs-toggle="modal"
            data-bs-target="#selfie"
          />

        </h6> */}

        <h6 className="align-items-center mb-1">
          <span
            className="pe-1 pb-2"
            style={{ color: '#111' }}
            data-bs-toggle="modal"
            data-bs-target="#selfie"
          >
            Click{' '}
            <a href="#" className="fs-16 text-underline">
              here
            </a>{' '}
            to know approved form of Selfie(s) with your License
          </span>
          {/* <FaInfoCircle
            data-bs-toggle="modal"
            data-bs-target="#selfie"
          /> */}
        </h6>
      </div>

      <div className="col-12 px-2">
        <div
          {...getAddressProofRootProps()}
          style={{
            border:
              errors.attachments && addressProofFiles?.length === 0
                ? '2px dashed red'
                : licenseFiles?.length > 0 &&
                    selfieFiles?.length >= 0 &&
                    addressProofFiles?.length == 0
                  ? '2px dashed #737373'
                  : '2px dashed #ccc',
            boxShadow:
              licenseFiles?.length > 0 &&
              selfieFiles?.length >= 0 &&
              addressProofFiles?.length == 0
                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                : 'none',
            padding: '8px',
            cursor: 'pointer',
            marginBottom: '10px',
            textAlign: 'center',
          }}
        >
          <input {...getAddressProofInputProps()} />
          <p className="mb-0">
            {isMobile
              ? 'Click to upload your Address proof'
              : 'Drag & drop your Address proof here, or Click to Select'}
          </p>
        </div>
        {/* <ul>
          {addressProofFiles?.map((file) => (
            <li key={file.name} className="mb-1">
              <img
                src={URL.createObjectURL(file.file)}
                width={200}
                height={200}
              />{' '}
              <br />
              {file.name}{' '}
              <span
                style={{ color: 'red', cursor: 'pointer' }}
                onClick={() => removeFile(file.name, 'address_proof')}
              >
                {' '}
                <i className="feather icon-x-circle " />
              </span>
            </li>
          ))}
        </ul> */}

        {addressProofFiles?.length > 0 && (
          <ImageSlider
            selectedFiles={addressProofFiles}
            name="address_proof"
            removeFile={removeFile}
            setLightboxSlide={setLightboxSlide}
            setLightboxIndex={setLightboxIndex}
          />
        )}
      </div>
      <div className="col-12 px-2 pt-1 mb-0">
        <h6 className="align-items-center mb-1 pb-2">
          <span
            className="pe-1"
            style={{ color: '#111' }}
            data-bs-toggle="modal"
            data-bs-target="#address_proof_validation"
          >
            Click{' '}
            <a href="#" className="fs-16  text-underline">
              here
            </a>{' '}
            to know approved form of Address proof{' '}
          </span>
        </h6>
      </div>

      <div className="booking-information-card">
        <div className="mb-1">
          <h6 className="align-items-center mb-1" style={{ color: '#111' }}>
            {' '}
            Billing Information{' '}
          </h6>
        </div>
        <div className="booking-info-body">
          <div className="row">
            <div className="col-6">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_first_name ? 'is-invalid' : ''}`}
                  placeholder="First Name"
                  {...register('billing_first_name')}
                />
              </div>
            </div>
            <div className="col-6 ps-1">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_last_name ? 'is-invalid' : ''}`}
                  placeholder="Last Name"
                  {...register('billing_last_name')}
                />
              </div>
            </div>

            <div className="col-8">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_email ? 'is-invalid' : ''}`}
                  placeholder="Email"
                  {...register('billing_email')}
                />
              </div>
            </div>
            <div className="col-4 ps-1">
              <div className="input-block">
                <div className="d-flex">
                  <input
                    type="number"
                    placeholder="Mobile"
                    className={`form-control mb-0  pe-0  ${isSubmitted && errors?.billing_mobile ? 'is-invalid' : ''}`}
                    {...register('billing_mobile', {
                      required: 'Mobile number is required',
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="booking-information-card  pt-0">
        <div className="mb-1">
          <h6 className="align-items-center mb-1" style={{ color: '#111' }}>
            {' '}
            Residential Address{' '}
          </h6>
        </div>
        <div className="booking-info-body">
          <div className="row">
            <div className="col-md-12">
              <div className="input-block">
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className={`form-control rounded-0 py-2  mb-0 ${errors.billing_address ? 'is-invalid' : ''}`}
                    {...register('billing_address')}
                    placeholder="Address"
                    aria-invalid={errors.billing_address ? 'true' : 'false'}
                  />
                </div>
              </div>
            </div>

            <div className="col-4">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_city ? 'is-invalid' : ''}`}
                  placeholder="City"
                  {...register('billing_city')}
                />
              </div>
            </div>
            <div className="col-4 ps-1">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_state ? 'is-invalid' : ''}`}
                  placeholder="State"
                  {...register('billing_state')}
                />
              </div>
            </div>
            <div className="col-4 ps-1">
              <div className="input-block">
                <input
                  type="text"
                  className={`form-control mb-0 ${isSubmitted && errors?.billing_pin_code ? 'is-invalid' : ''}`}
                  placeholder="Zip"
                  {...register('billing_pin_code')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`booking-information-card ${watch('use_same_residency_address') ? 'py-0' : 'pt-1'}`}
      >
        <div
          className={`${watch('use_same_residency_address') ? 'mb-0' : 'mb-1'}`}
        >
          {!watch('use_same_residency_address') && (
            <h6 className="align-items-center mb-1" style={{ color: '#111' }}>
              {' '}
              Car Parking Location 
            </h6>
          )}
          <div
            className={`input-block   ${watch('use_same_residency_address') ? 'mb-0' : 'mb-2'}`}
          >
            <label className="custom_check d-inline-flex location-check m-0">
              <span>
                <small className="text-sm  ">
                  <span>Same as Residential Address </span>
                </small>{' '}
              </span>

              <input
                type="checkbox"
                className={`form-control mb-0 ${errors.use_same_residency_address ? 'is-invalid' : ''}`}
                {...register('use_same_residency_address')}
              />
              <span
                style={{ padding: 5 }}
                className={`checkmark form-control ${errors.use_same_residency_address ? 'is-invalid' : ''}`}
              />
            </label>
          </div>
        </div>
        {!watch('use_same_residency_address') && (
          <div className="booking-info-body">
            <div className="row">
              <div className="col-md-12">
                <div className="input-block">
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      className={`form-control rounded-0 py-2  mb-0 ${errors.parking_address ? 'is-invalid' : ''}`}
                      {...register('parking_address')}
                      placeholder="Address"
                      aria-invalid={errors.parking_address ? 'true' : 'false'}
                    />
                  </div>
                </div>
              </div>

              <div className="col-4">
                <div className="input-block">
                  <input
                    type="text"
                    className={`form-control mb-0 ${isSubmitted && errors?.parking_city ? 'is-invalid' : ''}`}
                    placeholder="City"
                    {...register('parking_city')}
                  />
                </div>
              </div>
              <div className="col-4 ps-1">
                <div className="input-block">
                  <input
                    type="text"
                    className={`form-control mb-0 ${isSubmitted && errors?.parking_state ? 'is-invalid' : ''}`}
                    placeholder="State"
                    {...register('parking_state')}
                  />
                </div>
              </div>
              <div className="col-4 ps-1">
                <div className="input-block">
                  <input
                    type="text"
                    className={`form-control mb-0 ${isSubmitted && errors?.parking_pin_code ? 'is-invalid' : ''}`}
                    placeholder="Zip"
                    {...register('parking_pin_code')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="booking-information-card">
        <div className="col-md-12 mb-2">
          <div className="input-block m-0">
            <label
              className="custom_check d-inline-flex location-check m-0"
              {...(!bookingsLoading && {
                'data-bs-toggle': 'modal',
                'data-bs-target': '#preliminary_agreement',
              })}
            >
              <span>
                I have Read and Accept Terms &amp; Conditions{' '}
                <span className="ms-1 text-danger"> *</span>
              </span>

              <input
                type="checkbox"
                name="billing_terms_conditions"
                className={`form-control mb-0 ${isSubmitted && errors?.billing_terms_conditions ? 'is-invalid' : ''}`}
                checked={!!watch('billing_terms_conditions')}
              />
              <span
                style={{ padding: 5 }}
                {...(!bookingsLoading && {
                  'data-bs-toggle': 'modal',
                  'data-bs-target': '#preliminary_agreement',
                })}
                className={`checkmark form-control  ${isSubmitted && errors?.billing_terms_conditions ? 'is-invalid' : ''}`}
              />
            </label>
          </div>
        </div>

        <div className="col-md-12 mt-1 mb-2">
          <div className="input-block m-0">
            <label className="custom_check d-inline-flex location-check m-0">
              <span>
                <small className="text-sm  ">
                  <span>
                    Yes, I’d like to receive SMS alerts from Fairental for
                    booking confirmations, payment updates, support messages,
                    and important reminders.
                  </span>
                </small>{' '}
                <br />
                <small className="text-sm  ">
                  <i>
                    <span>
                      {' '}
                      Message and data rates may apply. Reply STOP to
                      unsubscribe.{' '}
                    </span>
                  </i>
                </small>
              </span>

              <input
                type="checkbox"
                className={`form-control mb-0 ${errors.is_sms_alert_approved ? 'is-invalid' : ''}`}
                {...register('is_sms_alert_approved')}
              />
              <span
                style={{ padding: 5 }}
                className={`checkmark form-control ${errors.is_sms_alert_approved ? 'is-invalid' : ''}`}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="booking-info-btns pt-2 px-2 d-flex justify-content-end">
        {bookingsLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <Lottie
              animationData={Loader}
              loop={true}
              style={{ width: 100, height: 100 }}
            />
          </div>
        ) : (
          <>
            {' '}
            <button
              // disabled={!watch('billing_terms_conditions')}
              id="booking_form_submit"
              type="submit"
              className="btn btn-primary mt-1 me-1"
            >
              Confirm &amp; Proceed
            </button>
          </>
        )}

        <button onClick={prevStep} className="btn btn-secondary mt-1">
          Back to Booking Review
        </button>
      </div>

      <PreliminaryAgreement
        clearSignature={clearSignature}
        handleAccept={handleAccept}
        showScrollTop={showScrollTop}
        handleScroll={handleScroll}
        scrollToTop={scrollToTop}
        scrollToBottom={scrollToBottom}
        BookingAgreement={BookingAgreement}
        isAccepted={isAccepted}
        termsRef={termsRef}
        signRef={signRef}
        loading={bookingsLoading}
      />

      <div
        className="modal new-modal fade"
        id="selfie"
        data-keyboard="false"
        data-backdrop="static"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header pb-1 mb-1">
              <h4 className="modal-title">Valid form of License with selfie</h4>
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                <span>×</span>
              </button>
            </div>
            <div className="modal-body">
              {templateList && (
                <div className="message-info py-1">
                  {' '}
                  <Selfie templateList={templateList} />{' '}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal new-modal fade"
        id="address_proof_validation"
        data-keyboard="false"
        data-backdrop="static"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header pb-1 mb-1">
              <h4 className="modal-title">Valid form of Address proof </h4>
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                <span>×</span>
              </button>
            </div>
            <div className="modal-body">
              {templateList && (
                <div className="message-info py-1">
                  {' '}
                  <AddressProofValidation templateList={templateList} />{' '}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Lightbox
        plugins={[Captions, Fullscreen, Zoom]}
        open={lightboxIndex !== null}
        close={() => setLightboxIndex(null)}
        index={lightboxIndex ?? 0}
        slides={lightboxSlide}
      />
    </>
  );
};

export default BookingDetail;
